import { useEffect, useState } from 'react';
import { ChevronDown /* , ExternalLink */ } from 'lucide-react';
import type { Player } from '@/types/game';
import { getTeamColors, /* sanitizePlayerUrl, */ hasTeamName, normalizeTeamName } from '@/utils/game-utils';
import { formatKd, kdColorClass, type PlayerStats } from '@/utils/player-stats';
import { RankBadge, rankRowClassName } from './RankRow';

interface PlayerCardProps {
  player: Player;
  rank: number;
  index: number;
  /** Kept for API compatibility — the row no longer draws score bars. */
  maxScore?: number;
  stats?: PlayerStats;
  /** Optionale Foto-URL; wird erst beim Aufklappen geladen und bei 404 verworfen. */
  photoUrl?: string | null;
}

const Tile = ({
  value,
  label,
  valueClass = 'text-foreground',
}: {
  value: string;
  label: string;
  valueClass?: string;
}) => (
  <div className="rounded-xl border border-border bg-surface-elevated p-2.5">
    <div className={`text-score text-lg leading-none ${valueClass}`}>{value}</div>
    <div className="mt-1 text-xs text-muted-foreground">{label}</div>
  </div>
);

export const PlayerCard = ({ player, rank, stats, photoUrl }: PlayerCardProps) => {
  const [open, setOpen] = useState(false);
  const [photoState, setPhotoState] = useState<'idle' | 'ok' | 'missing'>('idle');

  // Foto erst beim Aufklappen anfordern. Vorab per Image()-Objekt prüfen, damit
  // im 404-Fall kein leerer Rahmen und kein kaputtes Bildsymbol aufblitzt.
  useEffect(() => {
    if (!open || !photoUrl || photoState !== 'idle') return;
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => !cancelled && setPhotoState('ok');
    probe.onerror = () => !cancelled && setPhotoState('missing');
    probe.src = photoUrl;
    return () => {
      cancelled = true;
    };
  }, [open, photoUrl, photoState]);

  const colors = getTeamColors(player.team);
  // const safeUrl = sanitizePlayerUrl(player.playerurl);
  const showTeam = hasTeamName(player.team);

  const score = parseInt(player.score, 10) || 0;
  const isNegative = score < 0;

  const s = stats ?? {};
  const hasHitStats = s.hitsDealt !== undefined && s.hitsTaken !== undefined;

  const toggle = () => setOpen((v) => !v);

  return (
    <div className={rankRowClassName(rank)}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        className="flex cursor-pointer items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
      >
        <RankBadge rank={rank} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-0.5">
            <h3 className="truncate text-base font-bold text-foreground">{player.name}</h3>
            {/**
             * Link zur externen Scorekarte (my.lasermaxx.com), auf Wunsch des Betreibers ausgeblendet.
             * Zum Reaktivieren diesen Block wieder einkommentieren und `safeUrl` sowie die Imports
             * `ExternalLink` / `sanitizePlayerUrl` oben freigeben.
             *
             * {safeUrl && (
             *   <a ...>
             *     <ExternalLink className="h-4 w-4" aria-hidden="true" />
             *   </a>
             * )}
             */}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {showTeam && (
              <span
                className={`h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current ${colors.text}`}
                aria-hidden="true"
              />
            )}
            <span className="truncate">
              {showTeam ? `Team ${normalizeTeamName(player.team)} · ` : ''}Weste #{player.weste}
            </span>
          </div>
        </div>

        <div className="flex-shrink-0 text-right">
          <div
            className={`text-score text-[30px] font-black leading-none tracking-[-0.02em] sm:text-[34px] ${isNegative ? 'text-destructive' : 'text-foreground'}`}
          >
            {score.toLocaleString('de-DE')}
          </div>
        </div>

        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </div>

      {open && (
        <div className="mt-3 border-t border-border pt-3">
          {photoState === 'ok' && photoUrl && (
            <div className="overflow-hidden rounded-xl">
              <img
                src={photoUrl}
                alt={`Foto von ${player.name}`}
                loading="lazy"
                decoding="async"
                onError={() => setPhotoState('missing')}
                className="w-full object-contain max-h-[90vh]"
              />
            </div>
          )}

          <div className={`grid grid-cols-2 gap-2 sm:grid-cols-4 ${photoState === 'ok' && photoUrl ? 'mt-3' : ''}`}>
            <Tile value={player.shots} label="Schüsse" />
            <Tile value={`${player.accurancy}%`} label="Trefferquote" />
            {hasHitStats && (
              <>
                <Tile value={String(s.hitsDealt)} label="Treffer ausgeteilt" />
                <Tile value={String(s.hitsTaken)} label="Treffer kassiert" />
                <Tile value={formatKd(s)} label="K/D" valueClass={kdColorClass(s)} />
              </>
            )}
            <Tile
              value={
                s.pointsPerShot !== undefined
                  ? s.pointsPerShot.toLocaleString('de-DE', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })
                  : '–'
              }
              label="Punkte pro Schuss"
            />
            {s.teamSharePercent !== undefined && (
              <Tile value={`${s.teamSharePercent}%`} label="Anteil am Teamscore" />
            )}
          </div>

          <div className="mt-3 space-y-1 text-sm">
            {hasHitStats ? (
              <>
                <div className="text-xs text-muted-foreground">
                  Nemesis:{' '}
                  {s.nemesis ? (
                    <span className="text-sm font-semibold text-foreground">{s.nemesis.name}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">niemand</span>
                  )}
                  {s.nemesis ? ` · ${s.nemesis.hits} Treffer` : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  Lieblingsopfer:{' '}
                  {s.favoriteVictim ? (
                    <span className="text-sm font-semibold text-foreground">
                      {s.favoriteVictim.name}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">niemand</span>
                  )}
                  {s.favoriteVictim ? ` · ${s.favoriteVictim.hits} Treffer` : ''}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Für dieses Spiel liegen keine Trefferdaten vor.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
